import { useCallback, useMemo, useRef } from 'react';
import { DragLine } from '../../components/dragline';
import { NextModalProvider } from '../../next-modal';
import { SIDE_BAR_WIDTH_MIN } from '../const';
import {
  ConversationContext,
  defaultConversationContext,
  useConversationContext,
} from '../hooks/context';
import { useLiteConversationIdByParentId } from '../hooks/hook';
import type { ConversationContextType } from '../hooks/types';
import { ChatUIStyle, SideBarType } from '../hooks/types';
import { LiteChatPage } from '../pages/lite-chat-page';
import { LiteChatSkeleton } from './skeleton';

export const LiteChatWrapper = () => {
  const {
    sideBarType,
    conversationId,
    spaceId,
    updateContext,
    navigateToChat,
    onSaveArticle,
    sideBarWidth,
    updateBlocks,
    openUpgrade,
  } = useConversationContext();

  const containerRef = useRef<HTMLDivElement>(null);
  const liteConversationId = useLiteConversationIdByParentId(conversationId);

  const context: ConversationContextType | null = useMemo(() => {
    if (!liteConversationId) return null;
    return {
      ...defaultConversationContext,
      conversationId: liteConversationId,
      style: ChatUIStyle.LITE,
      spaceId,
      updateBlocks,
      navigateToChat,
      updateContext,
      onSaveArticle,
      openUpgrade,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, spaceId, liteConversationId]);

  const onClose = useCallback(() => {
    updateContext({ sideBarType: SideBarType.OUTLINE });
  }, [updateContext]);

  const handleWidthChange = useCallback(
    (width: number) => {
      updateContext({ sideBarWidth: width });
    },
    [updateContext]
  );

  if (sideBarType === SideBarType.LITE_CHAT_SKELETON) {
    return <LiteChatSkeleton />;
  }

  if (sideBarType !== SideBarType.LITE_CHAT) {
    return null;
  }

  if (!context) return <LiteChatSkeleton />;

  return (
    <div className="flex items-center">
      <div className="w-2 bg-aside h-full"></div>
      <div
        ref={containerRef}
        className="h-screen w-full flex-shrink-0 border-l relative max-w-full"
        style={{ width: sideBarWidth, minWidth: SIDE_BAR_WIDTH_MIN }}
      >
        <DragLine
          className="-left-0.5"
          container={containerRef}
          onChange={handleWidthChange}
          alignLeft={false}
        />
        <ConversationContext.Provider value={context}>
          <NextModalProvider>
            <LiteChatPage onClose={onClose} />
          </NextModalProvider>
        </ConversationContext.Provider>
      </div>
    </div>
  );
};
